import { CommunicationConnectionDescriptor } from "./communication.ts";
import { EmailActionInput, EmailSetupStatus } from "./email.ts";
import { ActivityId } from "./baseSchemas.ts";
import {
  SourceControlConnectionAction,
  SourceControlConnectionResult,
} from "./sourceControlConnections.ts";
import {
  OperatorEnvironmentActionInput,
  OperatorEnvironmentActionResult,
  OperatorKnowledgeActionInput,
  OperatorKnowledgeActionResult,
  OperatorMemoryActionInput,
  OperatorMemoryActionResult,
} from "./learning.ts";
import * as Schema from "effect/Schema";
import * as Rpc from "effect/unstable/rpc/Rpc";
import * as RpcGroup from "effect/unstable/rpc/RpcGroup";

import { EnvironmentAuthorizationError } from "./auth.ts";

import { AssetAccessError, AssetCreateUrlInput, AssetCreateUrlResult } from "./assets.ts";

import { KeybindingsConfigError } from "./keybindings.ts";

import {
  Conversation,
  HarnessConnection,
  OPERATOR_WS_METHODS,
  OperatorCommand,
  OperatorCommandReceipt,
  OperatorRpcError,
  OperatorSnapshot,
  OperatorHistoryPage,
  OperatorStreamItem,
  OperatorConnectionsSnapshot,
  OperatorSkillActionInput,
  OperatorSkillActionResult,
  OperatorConnectionActionInput,
  OperatorConnectionActionResult,
  OperatorProviderConfiguration,
  OperatorProviderConfigurationInput,
  OperatorProviderActivationInput,
  OperatorProviderReasoningEffortInput,
  OperatorProviderDisconnectInput,
  OperatorProviderOAuthActionInput,
  OperatorProviderOAuthActionResult,
  OperatorAutomation,
  OperatorAutomationRunsInput,
  OperatorAutomationRunsResult,
  OperatorAutomationRunOpenInput,
  OperatorAutomationRunOpenResult,
  OperatorAutomationActionResult,
  OperatorAutomationActionInput,
  OperatorLoopWebhookActionInput,
  OperatorLoopWebhookActionResult,
  OperatorVaultSecret,
  OperatorVaultSecretDeleteInput,
  OperatorVaultSecretUpsertInput,
  WorkspaceTaskDetail,
} from "./operator.ts";
import { SlackActionInput, SlackActionResult, SlackSetupStatus } from "./slack.ts";
import { WorkspaceMcpConsentInput, WorkspaceMcpConsentResult } from "./workspaceMcp.ts";
import {
  WorkspaceLogQuery,
  WorkspaceLogPage,
  WorkspaceLogExport,
  WorkspaceLogPolicy,
  WorkspaceLogPolicyStatus,
  WorkspaceLogError,
} from "./workspaceLogs.ts";
import {
  ConversationId,
  HarnessConnectionId,
  OperatorProjectId,
  TrimmedNonEmptyString,
} from "./baseSchemas.ts";

import { ServerConfig } from "./server.ts";

import { ServerSettingsError } from "./settings.ts";

export const WS_METHODS = {
  serverProbe: "server.probe",
  serverGetConfig: "server.getConfig",
  assetsCreateUrl: "assets.createUrl",
} as const;

export const WsServerProbeRpc = Rpc.make(WS_METHODS.serverProbe, {
  payload: Schema.Struct({}),
  success: Schema.Struct({}),
  error: EnvironmentAuthorizationError,
});

export const WsServerGetConfigRpc = Rpc.make(WS_METHODS.serverGetConfig, {
  payload: Schema.Struct({}),
  success: ServerConfig,
  error: Schema.Union([KeybindingsConfigError, ServerSettingsError, EnvironmentAuthorizationError]),
});

export const WsAssetsCreateUrlRpc = Rpc.make(WS_METHODS.assetsCreateUrl, {
  payload: AssetCreateUrlInput,
  success: AssetCreateUrlResult,
  error: Schema.Union([AssetAccessError, EnvironmentAuthorizationError]),
});

export const WsOperatorGetSnapshotRpc = Rpc.make(OPERATOR_WS_METHODS.getSnapshot, {
  payload: Schema.Struct({}),
  success: OperatorSnapshot,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorGetConversationHistoryRpc = Rpc.make(
  OPERATOR_WS_METHODS.getConversationHistory,
  {
    payload: Schema.Struct({ conversationId: ConversationId, beforeActivityId: ActivityId }),
    success: OperatorHistoryPage,
    error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
  },
);

export const WsOperatorOpenConversationRpc = Rpc.make(OPERATOR_WS_METHODS.openConversation, {
  payload: Schema.Struct({ conversationId: ConversationId }),
  success: Conversation,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorGetWorkspaceTaskRpc = Rpc.make(OPERATOR_WS_METHODS.getWorkspaceTask, {
  payload: Schema.Struct({
    projectId: OperatorProjectId,
    taskId: TrimmedNonEmptyString,
  }),
  success: WorkspaceTaskDetail,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorListConnectionsRpc = Rpc.make(OPERATOR_WS_METHODS.listConnections, {
  payload: Schema.Struct({ projectId: OperatorProjectId }),
  success: OperatorConnectionsSnapshot,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorSourceControlActionRpc = Rpc.make(OPERATOR_WS_METHODS.sourceControlAction, {
  payload: SourceControlConnectionAction,
  success: SourceControlConnectionResult,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorMemoryActionRpc = Rpc.make(OPERATOR_WS_METHODS.memoryAction, {
  payload: OperatorMemoryActionInput,
  success: OperatorMemoryActionResult,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorEnvironmentActionRpc = Rpc.make(OPERATOR_WS_METHODS.environmentAction, {
  payload: OperatorEnvironmentActionInput,
  success: OperatorEnvironmentActionResult,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorKnowledgeActionRpc = Rpc.make(OPERATOR_WS_METHODS.knowledgeAction, {
  payload: OperatorKnowledgeActionInput,
  success: OperatorKnowledgeActionResult,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorSkillActionRpc = Rpc.make(OPERATOR_WS_METHODS.skillAction, {
  payload: OperatorSkillActionInput,
  success: OperatorSkillActionResult,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorConnectionActionRpc = Rpc.make(OPERATOR_WS_METHODS.connectionAction, {
  payload: OperatorConnectionActionInput,
  success: OperatorConnectionActionResult,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorWorkspaceMcpConsentRpc = Rpc.make(OPERATOR_WS_METHODS.workspaceMcpConsent, {
  payload: WorkspaceMcpConsentInput,
  success: WorkspaceMcpConsentResult,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorGetProviderConfigurationRpc = Rpc.make(
  OPERATOR_WS_METHODS.getProviderConfiguration,
  {
    payload: Schema.Struct({
      projectId: Schema.optionalKey(OperatorProjectId),
      purpose: Schema.optionalKey(Schema.Literal("coding")),
    }),
    success: OperatorProviderConfiguration,
    error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
  },
);

export const WsOperatorSaveProviderConfigurationRpc = Rpc.make(
  OPERATOR_WS_METHODS.saveProviderConfiguration,
  {
    payload: OperatorProviderConfigurationInput,
    success: OperatorProviderConfiguration,
    error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
  },
);

export const WsOperatorActivateProviderRpc = Rpc.make(OPERATOR_WS_METHODS.activateProvider, {
  payload: OperatorProviderActivationInput,
  success: OperatorProviderConfiguration,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorSetProviderReasoningEffortRpc = Rpc.make(
  OPERATOR_WS_METHODS.setProviderReasoningEffort,
  {
    payload: OperatorProviderReasoningEffortInput,
    success: OperatorProviderConfiguration,
    error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
  },
);

export const WsOperatorDisconnectProviderRpc = Rpc.make(OPERATOR_WS_METHODS.disconnectProvider, {
  payload: OperatorProviderDisconnectInput,
  success: OperatorProviderConfiguration,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorProviderOAuthActionRpc = Rpc.make(OPERATOR_WS_METHODS.providerOAuthAction, {
  payload: OperatorProviderOAuthActionInput,
  success: OperatorProviderOAuthActionResult,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorListAutomationsRpc = Rpc.make(OPERATOR_WS_METHODS.listAutomations, {
  payload: Schema.Struct({ projectId: OperatorProjectId }),
  success: Schema.Array(OperatorAutomation),
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorQueryLogsRpc = Rpc.make(OPERATOR_WS_METHODS.queryLogs, {
  payload: WorkspaceLogQuery,
  success: WorkspaceLogPage,
  error: Schema.Union([WorkspaceLogError, OperatorRpcError, EnvironmentAuthorizationError]),
});
export const WsOperatorExportLogsRpc = Rpc.make(OPERATOR_WS_METHODS.exportLogs, {
  payload: WorkspaceLogQuery,
  success: WorkspaceLogExport,
  error: Schema.Union([WorkspaceLogError, OperatorRpcError, EnvironmentAuthorizationError]),
});
export const WsOperatorGetLogPolicyRpc = Rpc.make(OPERATOR_WS_METHODS.getLogPolicy, {
  payload: Schema.Struct({ projectId: OperatorProjectId }),
  success: WorkspaceLogPolicyStatus,
  error: Schema.Union([WorkspaceLogError, OperatorRpcError, EnvironmentAuthorizationError]),
});
export const WsOperatorSetLogPolicyRpc = Rpc.make(OPERATOR_WS_METHODS.setLogPolicy, {
  payload: WorkspaceLogPolicy,
  success: WorkspaceLogPolicy,
  error: Schema.Union([WorkspaceLogError, OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorListAutomationRunsRpc = Rpc.make(OPERATOR_WS_METHODS.listAutomationRuns, {
  payload: OperatorAutomationRunsInput,
  success: OperatorAutomationRunsResult,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorOpenAutomationRunRpc = Rpc.make(OPERATOR_WS_METHODS.openAutomationRun, {
  payload: OperatorAutomationRunOpenInput,
  success: OperatorAutomationRunOpenResult,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorAutomationActionRpc = Rpc.make(OPERATOR_WS_METHODS.automationAction, {
  payload: OperatorAutomationActionInput,
  success: OperatorAutomationActionResult,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorLoopWebhookActionRpc = Rpc.make(OPERATOR_WS_METHODS.loopWebhookAction, {
  payload: OperatorLoopWebhookActionInput,
  success: OperatorLoopWebhookActionResult,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorCommunicationConnectionsRpc = Rpc.make(
  OPERATOR_WS_METHODS.communicationConnections,
  {
    payload: Schema.Struct({ projectId: OperatorProjectId }),
    success: Schema.Array(CommunicationConnectionDescriptor),
    error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
  },
);

export const WsOperatorGetSlackSetupRpc = Rpc.make(OPERATOR_WS_METHODS.getSlackSetup, {
  payload: Schema.Struct({ projectId: OperatorProjectId }),
  success: SlackSetupStatus,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorSlackActionRpc = Rpc.make(OPERATOR_WS_METHODS.slackAction, {
  payload: SlackActionInput,
  success: SlackActionResult,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorGetEmailSetupRpc = Rpc.make(OPERATOR_WS_METHODS.getEmailSetup, {
  payload: Schema.Struct({ projectId: OperatorProjectId }),
  success: EmailSetupStatus,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});
export const WsOperatorEmailActionRpc = Rpc.make(OPERATOR_WS_METHODS.emailAction, {
  payload: EmailActionInput,
  success: EmailSetupStatus,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorListVaultSecretsRpc = Rpc.make(OPERATOR_WS_METHODS.listVaultSecrets, {
  payload: Schema.Struct({}),
  success: Schema.Array(OperatorVaultSecret),
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorUpsertVaultSecretRpc = Rpc.make(OPERATOR_WS_METHODS.upsertVaultSecret, {
  payload: OperatorVaultSecretUpsertInput,
  success: OperatorVaultSecret,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorDeleteVaultSecretRpc = Rpc.make(OPERATOR_WS_METHODS.deleteVaultSecret, {
  payload: OperatorVaultSecretDeleteInput,
  success: Schema.Void,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorReconnectRpc = Rpc.make(OPERATOR_WS_METHODS.reconnect, {
  payload: Schema.Struct({ connectionId: HarnessConnectionId }),
  success: HarnessConnection,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorDispatchCommandRpc = Rpc.make(OPERATOR_WS_METHODS.dispatchCommand, {
  payload: OperatorCommand,
  success: OperatorCommandReceipt,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
});

export const WsOperatorSubscribeRpc = Rpc.make(OPERATOR_WS_METHODS.subscribe, {
  payload: Schema.Struct({
    afterSequence: Schema.optionalKey(Schema.Number),
    deltas: Schema.optionalKey(Schema.Boolean),
    windowedHistory: Schema.optionalKey(Schema.Boolean),
    settingsOnly: Schema.optionalKey(Schema.Boolean),
  }),
  success: OperatorStreamItem,
  error: Schema.Union([OperatorRpcError, EnvironmentAuthorizationError]),
  stream: true,
});

/** Runtime surface shared by web settings and authorized bot/MCP clients. */
export const OperatorWsRpcGroup = RpcGroup.make(
  WsAssetsCreateUrlRpc,
  WsServerProbeRpc,
  WsServerGetConfigRpc,
  WsOperatorGetSnapshotRpc,
  WsOperatorOpenConversationRpc,
  WsOperatorGetConversationHistoryRpc,
  WsOperatorGetWorkspaceTaskRpc,
  WsOperatorListConnectionsRpc,
  WsOperatorSourceControlActionRpc,
  WsOperatorConnectionActionRpc,
  WsOperatorWorkspaceMcpConsentRpc,
  WsOperatorSkillActionRpc,
  WsOperatorEnvironmentActionRpc,
  WsOperatorKnowledgeActionRpc,
  WsOperatorMemoryActionRpc,
  WsOperatorGetProviderConfigurationRpc,
  WsOperatorSaveProviderConfigurationRpc,
  WsOperatorActivateProviderRpc,
  WsOperatorSetProviderReasoningEffortRpc,
  WsOperatorDisconnectProviderRpc,
  WsOperatorProviderOAuthActionRpc,
  WsOperatorListAutomationsRpc,
  WsOperatorQueryLogsRpc,
  WsOperatorExportLogsRpc,
  WsOperatorGetLogPolicyRpc,
  WsOperatorSetLogPolicyRpc,
  WsOperatorListAutomationRunsRpc,
  WsOperatorOpenAutomationRunRpc,
  WsOperatorAutomationActionRpc,
  WsOperatorLoopWebhookActionRpc,
  WsOperatorCommunicationConnectionsRpc,
  WsOperatorGetEmailSetupRpc,
  WsOperatorEmailActionRpc,
  WsOperatorGetSlackSetupRpc,
  WsOperatorSlackActionRpc,
  WsOperatorListVaultSecretsRpc,
  WsOperatorUpsertVaultSecretRpc,
  WsOperatorDeleteVaultSecretRpc,
  WsOperatorReconnectRpc,
  WsOperatorDispatchCommandRpc,
  WsOperatorSubscribeRpc,
);

export const WsRpcGroup = OperatorWsRpcGroup;
